<template>
  <div class="mt-4 space-y-4">
    <div class="grid gap-4 sm:grid-cols-3">
      <LabeledText :label="t('date')">{{ formatDate(payment.date, dateFormat) }}</LabeledText>
      <LabeledText :label="t('amount')">
        <span v-currency="payment.amount" class="font-bold"></span>
        {{
          payment.refundedAmount > 0
            ? `(${t('refunded', { amount: formatCurrency(payment.refundedAmount) })})`
            : ''
        }}
      </LabeledText>
      <LabeledText :label="t('status')" :info-text="t(`statusDescription.${payment.status}`)">
        <PaymentStatusBadge :status="payment.status" size="sm" class="text-sm" />
      </LabeledText>
      <LabeledText :label="t('comment')">{{ payment.comment ?? '-' }}</LabeledText>
      <LabeledText :label="t('paymentType')">
        <span class="flex items-center gap-2">
          <WalletProviderIcon v-if="payment.walletProvider" :provider="payment.walletProvider" />
          {{ t(`paymentTypes.${payment.paymentType}`) }}
          <span v-if="payment.creditCardDetails?.source"
            >({{ payment.creditCardDetails?.source }})</span
          >
        </span>
      </LabeledText>
      <LabeledText :label="t('referenceId')">{{ payment.referenceId ?? '-' }}</LabeledText>
      <LabeledText :label="t('invoice')">{{ invoice?.completeInvoiceNumber ?? '-' }}</LabeledText>
      <LabeledText :label="t('counterpartyType.label')">{{
        payment.counterpartyType ? t(`counterpartyTypes.${payment.counterpartyType}`) : '-'
      }}</LabeledText>
      <LabeledText v-if="$can('list', 'PaymentAllocation')" :label="t('toAllocate')"
        ><span v-if="paymentAllocations">{{ formatCurrency(toAllocate) }}</span
        ><Spinner v-else class="size-4"
      /></LabeledText>
      <LabeledText v-if="payment.paymentLinkUrl" :label="t('paymentLink')">
        <div class="flex min-w-0 items-center gap-1">
          <span class="min-w-0 truncate" :title="payment.paymentLinkUrl">{{
            payment.paymentLinkUrl
          }}</span>
          <CopyToClipboardButton
            :text="payment.paymentLinkUrl"
            class="shrink-0 text-obsidian-500"
          />
        </div>
      </LabeledText>
    </div>
    <Collapsible
      v-if="payment.creditCardDetails"
      v-model="showCreditCardData"
      :title="t('creditCardDetails')"
      variant="compact"
    >
      <CreditCardDetails :credit-card-details="payment.creditCardDetails" />
    </Collapsible>
    <Collapsible
      v-if="$can('list', 'PaymentAllocation')"
      v-model="showLedgerEntries"
      :title="t('ledgerAllocations')"
      variant="compact"
    >
      <div v-if="!showSelectLedgerEntries && !showRefundForm" class="space-y-4">
        <LedgerEntryAllocations :payment-id="payment.id" />
        <div class="flex flex-wrap items-center justify-end gap-4">
          <div class="font-bold">
            <div v-if="toAllocate !== 0">
              {{ t('openAmount') }} {{ formatCurrency(toAllocate) }}
            </div>
            <div v-else class="flex items-center gap-2">
              {{ t('allocatedEntirely') }}
              <div
                class="flex size-5 items-center justify-center rounded-full bg-green-700 text-white"
              >
                <CheckIcon class="size-4" />
              </div>
            </div>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-4">
            <CVButton
              v-if="$can('create', 'PaymentAllocation')"
              @click.prevent="
                showSelectLedgerEntries = true;
                showRefundForm = false;
              "
              >{{ t('allocateAmount') }}</CVButton
            >
            <CVButton
              v-if="canRefundUnallocated"
              variant="ghost"
              @click.prevent="
                showRefundForm = true;
                showSelectLedgerEntries = false;
              "
              >{{ t('refundOpenButton') }}</CVButton
            >
          </div>
        </div>
      </div>
      <div v-else-if="showRefundForm" class="mt-2 space-y-4">
        <p class="text-sm text-obsidian-600">{{ t('refundUnallocatedHint') }}</p>
        <Input
          v-model="refundAmountEuros"
          type="number"
          :min-fraction-digits="2"
          :max-fraction-digits="2"
          :min="0.01"
          :max="maxRefundEuros"
          :step="0.01"
          adornment="€"
          :label="t('refundAmount')"
          class="max-w-xs"
        />
        <div class="flex flex-wrap items-center justify-end gap-4">
          <CVButton
            :disabled="!isRefundAmountValid"
            :is-loading="isRefunding"
            variant="default"
            @click="handleRefundUnallocated"
          >
            {{ t('refundSubmit') }}
          </CVButton>
          <CVButton @click.prevent="showRefundForm = false">{{ t('cancel') }}</CVButton>
        </div>
      </div>
      <div v-else class="mt-2 space-y-4">
        <AllocateToLedgerEntry
          :payment="payment"
          :scope="allocationScope"
          @success="
            showSelectLedgerEntries = false;
            showRefundForm = false;
          "
          @cancel="
            showSelectLedgerEntries = false;
            showRefundForm = false;
          "
        />
      </div>
    </Collapsible>
    <div v-if="canCancelPayment" class="border-t border-obsidian-200 pt-4">
      <CVButton variant="danger" :is-loading="isCancelling" @click="handleCancelPayment">
        {{ t('cancelPayment') }}
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Payment } from '@/entities/payments/payment.entity';
import PaymentStatusBadge from '@/components/badges/PaymentStatusBadge.vue';
import { PaymentStatus } from '@/entities/payments/payment-status.enum';
import LabeledText from '@/components/LabeledText.vue';
import WalletProviderIcon from '@/components/icons/WalletProviderIcon.vue';
import Collapsible from '@/components/Collapsible.vue';
import CreditCardDetails from './CreditCardDetails.vue';
import { formatDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import LedgerEntryAllocations from '../ledger-entries/LedgerEntryAllocations.vue';
import AllocateToLedgerEntry from '../ledger-entries/AllocateToLedgerEntry.vue';
import CVButton from '../buttons/CVButton.vue';
import { usePaymentAllocations } from '@/queries/use-ledger';
import { sumBy } from 'lodash';
import { formatCurrency } from '@/utils/format-numbers';
import Spinner from '../icons/Spinner.vue';
import { CheckIcon } from 'lucide-vue-next';
import { useDateFormat } from '@/hooks/use-date-format.ts';
import { useRefundUnallocatedPaymentAmount, useUpdatePayment } from '@/queries/use-payments';
import { Alert } from '@/services/alert.service';
import { useAbility } from '@casl/vue';
import type { Invoice } from '@/entities/invoices/invoice.entity';
import Input from '@/components/Input.vue';
import { roundDecimals } from '@/utils/round-decimals';
import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';
import type { AllocationScope } from '../ledger-entries/utils/booking-or-invoice-scope';

const props = withDefaults(
  defineProps<{
    payment: Payment;
    bookingId?: string;
    invoice?: Invoice;
    initialShowRefund?: boolean;
    /** Allocate to ledger entries of any booking. Used when the payment has no booking. */
    allocateAcrossBookings?: boolean;
    open?: boolean;
  }>(),
  {
    open: true,
    bookingId: undefined,
    invoice: undefined,
    initialShowRefund: false,
    allocateAcrossBookings: false,
  },
);

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useI18n();
const dateFormat = useDateFormat();
const ability = useAbility();

const showCreditCardData = ref(false);
const showLedgerEntries = ref(!!props.initialShowRefund || !!props.allocateAcrossBookings);
const showSelectLedgerEntries = ref(false);
const showRefundForm = ref(!!props.initialShowRefund);

const { data: paymentAllocations } = usePaymentAllocations(
  computed(() => props.payment.id),
  ability.can('list', 'PaymentAllocation'),
);

const { mutateAsync: updatePayment, isPending: isCancelling } = useUpdatePayment();
const { mutateAsync: refundUnallocated, isPending: isRefunding } =
  useRefundUnallocatedPaymentAmount();

const toAllocate = computed(
  () => props.payment.amount - sumBy(paymentAllocations.value ?? [], 'amount') / 100,
);

const refundAmountEuros = ref(0);

const maxRefundEuros = computed(() =>
  Math.max(0, Math.min(toAllocate.value, props.payment.amount - props.payment.refundedAmount)),
);

const canRefundUnallocated = computed(
  () =>
    props.payment.status === PaymentStatus.PAID &&
    toAllocate.value > 0 &&
    ability.can('refund', 'Payment') &&
    !props.payment.isManualPayment,
);

const isRefundAmountValid = computed(
  () =>
    refundAmountEuros.value > 0 &&
    roundDecimals(refundAmountEuros.value, 2) <= roundDecimals(maxRefundEuros.value, 2),
);

const canCancelPayment = computed(() => {
  if (props.payment.status !== PaymentStatus.PAID) return false;
  if (!props.payment.isManualPayment) return false;
  return true;
});

const invoice = computed(() => props.invoice ?? props.payment.invoice);

const allocationScope = computed<AllocationScope>(() => {
  if (props.allocateAcrossBookings) {
    return { anyBooking: true };
  }
  if (props.bookingId) {
    return { bookingId: props.bookingId };
  }
  return { invoiceId: invoice.value!.id };
});

const handleRefundUnallocated = async () => {
  const { isConfirmed } = await Alert.fire({
    titleText: t('refundUnallocatedConfirmTitle'),
    text: t('refundUnallocatedConfirmText', {
      amount: formatCurrency(refundAmountEuros.value),
    }),
    icon: 'warning',
    showCancelButton: true,
    cancelButtonText: t('dontCancel'),
    confirmButtonText: t('refundSubmit'),
  });
  if (!isConfirmed || !props.payment.id) return;
  await refundUnallocated({
    paymentId: props.payment.id,
    amount: refundAmountEuros.value,
  });
  showRefundForm.value = false;
};

const handleCancelPayment = async () => {
  const alertResult = await Alert.fire({
    titleText: t('reallyCancelPaymentTitle'),
    text: t('reallyCancelPaymentText'),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('cancelPayment'),
    cancelButtonText: t('dontCancel'),
  });
  if (alertResult.isDenied && props.payment.id) {
    await updatePayment({
      id: props.payment.id,
      payment: { status: PaymentStatus.CANCELLED },
    });
    emit('close');
  }
};

const resetPanels = () => {
  showCreditCardData.value = false;
  showSelectLedgerEntries.value = false;
  if (props.initialShowRefund) {
    showLedgerEntries.value = true;
    showRefundForm.value = true;
  } else {
    showLedgerEntries.value = !!props.allocateAcrossBookings;
    showRefundForm.value = false;
  }
};

watch(
  () => props.open,
  (val) => {
    if (val) resetPanels();
  },
);

watch(
  [showRefundForm, maxRefundEuros] as const,
  ([open, maxRefund]) => {
    if (open && maxRefund > 0) {
      refundAmountEuros.value = Math.round(maxRefund * 100) / 100;
    }
  },
  { immediate: true },
);
</script>

<i18n lang="json">
{
  "en": {
    "date": "Date",
    "amount": "Amount",
    "refunded": "Of which {amount} refunded",
    "paymentType": "Payment Type",
    "invoice": "Invoice",
    "referenceId": "Reference ID",
    "paymentLink": "Payment Link",
    "comment": "Comment",
    "ledgerAllocations": "Ledger Allocations",
    "creditCardDetails": "Credit Card Details",
    "status": "Status",
    "statusDescription": {
      "PENDING": "The payment is still pending.",
      "AUTHORIZED": "The payment has been authorized by the customer but not yet captured.",
      "CAPTURE_REQUESTED": "The capture request has been sent to the payment provider.",
      "PAID": "The payment has been successfully received.",
      "CANCELLED": "The payment has been cancelled."
    },
    "toAllocate": "To Allocate",
    "allocateAmount": "Allocate Amount",
    "openAmount": "Open amount",
    "allocatedEntirely": "Allocated entirely",
    "refundOpenButton": "Refund amount",
    "refundSubmit": "Refund",
    "refundUnallocatedHint": "Refund the portion of this payment that is not yet allocated to receivables. The amount is capped by what is still open and what the payment provider allows.",
    "refundAmount": "Refund amount",
    "refundUnallocatedConfirmTitle": "Refund unallocated amount?",
    "refundUnallocatedConfirmText": "The amount {amount} will be refunded to the customer and booked via a netting entry.",
    "cancelPayment": "Cancel manual payment for correction",
    "reallyCancelPaymentTitle": "Really cancel manual payment for correction?",
    "reallyCancelPaymentText": "The manual payment will be cancelled. NO automatic refund will be issued. Allocations will be reversed.",
    "dontCancel": "Don't cancel",
    "cancel": "Cancel"
  },
  "de": {
    "date": "Datum",
    "amount": "Betrag",
    "refunded": "Davon {amount} erstattet",
    "paymentType": "Zahlungsart",
    "invoice": "Rechnung",
    "referenceId": "Referenz-ID",
    "paymentLink": "Zahlungslink",
    "comment": "Kommentar",
    "ledgerAllocations": "Forderungszuordnungen",
    "creditCardDetails": "Kreditkarten Details",
    "status": "Status",
    "statusDescription": {
      "PENDING": "Bestätigung für die Zahlung ist noch ausstehend.",
      "AUTHORIZED": "Die Zahlung wurde vom Kunden autorisiert, aber noch nicht eingezogen.",
      "CAPTURE_REQUESTED": "Das Abschließen / Einziehen der Zahlung wurde angefragt.",
      "PAID": "Die Zahlung ist erfolgreich eingegangen.",
      "CANCELLED": "Die Zahlung wurde storniert."
    },
    "toAllocate": "Noch nicht zugeordnet",
    "allocateAmount": "Betrag zuordnen",
    "openAmount": "Noch offener Betrag",
    "allocatedEntirely": "Vollständig zugeordnet",
    "refundOpenButton": "Betrag erstatten",
    "refundSubmit": "Erstatten",
    "refundUnallocatedHint": "Erstattet den Teil dieser Zahlung, der noch keiner Forderung zugeordnet ist. Der Betrag ist begrenzt durch den offenen Rest und die Möglichkeiten des Zahlungsanbieters.",
    "refundAmount": "Erstattungsbetrag",
    "refundUnallocatedConfirmTitle": "Nicht zugeordneten Betrag erstatten?",
    "refundUnallocatedConfirmText": "Der Betrag {amount} wird an den Kunden zurückerstattet und über eine Verrechnungsposition gebucht.",
    "cancelPayment": "Manuelle Zahlung für Korrektur stornieren",
    "reallyCancelPaymentTitle": "Manuelle Zahlung wirklich stornieren?",
    "reallyCancelPaymentText": "Die manuelle Zahlung wird storniert. Es erfolgt KEINE automatische Erstattung. Zuordnungen werden rückgängig gemacht.",
    "dontCancel": "Nicht stornieren",
    "cancel": "Abbrechen"
  },
  "es": {
    "date": "Fecha",
    "amount": "Importe",
    "refunded": "Importe reembolsado: {amount}",
    "paymentType": "Tipo de pago",
    "invoice": "Factura",
    "referenceId": "ID de referencia",
    "paymentLink": "Enlace de pago",
    "comment": "Comentario",
    "ledgerAllocations": "Imputaciones contables",
    "creditCardDetails": "Datos de la tarjeta",
    "status": "Estado",
    "statusDescription": {
      "PENDING": "La confirmación del pago sigue pendiente.",
      "AUTHORIZED": "El cliente ha autorizado el pago, pero aún no se ha cobrado.",
      "CAPTURE_REQUESTED": "Se ha enviado la solicitud de cobro al proveedor de pagos.",
      "PAID": "El pago se ha recibido correctamente.",
      "CANCELLED": "El pago se ha cancelado."
    },
    "toAllocate": "Por imputar",
    "allocateAmount": "Imputar importe",
    "openAmount": "Importe pendiente",
    "allocatedEntirely": "Totalmente imputado",
    "refundOpenButton": "Reembolsar importe",
    "refundSubmit": "Reembolsar",
    "refundUnallocatedHint": "Reembolsa la parte de este pago que aún no está imputada a cobros. El importe queda limitado por lo pendiente y por lo que permite el proveedor de pagos.",
    "refundAmount": "Importe a reembolsar",
    "refundUnallocatedConfirmTitle": "¿Reembolsar el importe no imputado?",
    "refundUnallocatedConfirmText": "Se reembolsará al cliente el importe {amount} y se contabilizará mediante una partida de compensación.",
    "cancelPayment": "Cancelar pago manual por corrección",
    "reallyCancelPaymentTitle": "¿Cancelar el pago manual por corrección?",
    "reallyCancelPaymentText": "Se cancelará el pago manual. NO se emitirá reembolso automático. Las imputaciones se revertirán.",
    "dontCancel": "No cancelar",
    "cancel": "Cancelar"
  }
}
</i18n>
